import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ShieldAlert, AlertTriangle, CheckCircle2, Settings2, PlayCircle,
  TrendingUp, Loader2, History, Clock, Database, FileDown, RefreshCw,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * P5 — Compliance Center (/admin/risk/compliance-center)
 *
 * Gathers in one place:
 *   - Compliance settings (block_mode toggle + gating policies)
 *   - Manual sweep triggers (KYC expiry, periodic re-screening)
 *   - Sweep history log
 *   - Quick gate checker for any counterparty
 */

const SETTING_FIELDS = [
  { key: 'require_kyc_approved',  label: 'Require approved KYC',           description: 'Block counterparties without an approved KYC record.' },
  { key: 'block_on_sanctions',    label: 'Block on open sanctions hits',    description: 'Block counterparties with unreviewed sanctions matches.' },
  { key: 'block_on_critical_risk',label: 'Block on critical risk level',    description: 'Block counterparties whose latest risk score is CRITICAL.' },
];


const ComplianceCenter = () => {
  const { toast } = useToast();
  const token = useMemo(() => localStorage.getItem('admin_token'), []);
  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [settings, setSettings] = useState(null);
  const [savingField, setSavingField] = useState(null);
  const [sweepLogs, setSweepLogs] = useState([]);
  const [running, setRunning] = useState(null);
  const [gateInput, setGateInput] = useState('');
  const [gateResult, setGateResult] = useState(null);
  const [gateLoading, setGateLoading] = useState(false);
  const [sanctionsLastRefresh, setSanctionsLastRefresh] = useState(null);
  const [sanctionsRefreshLogs, setSanctionsRefreshLogs] = useState([]);
  const [refreshingSanctions, setRefreshingSanctions] = useState(false);
  const [downloadingAudit, setDownloadingAudit] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, l, lr, rl] = await Promise.all([
        axios.get(`${API_URL}/api/risk/settings`, authHeader),
        axios.get(`${API_URL}/api/risk/sweep/logs?limit=10`, authHeader),
        axios.get(`${API_URL}/api/risk/sanctions/last-refresh`, authHeader),
        axios.get(`${API_URL}/api/risk/sanctions/refresh-logs?limit=5`, authHeader),
      ]);
      setSettings(s.data);
      setSweepLogs(l.data || []);
      setSanctionsLastRefresh(lr.data && Object.keys(lr.data).length ? lr.data : null);
      setSanctionsRefreshLogs(rl.data || []);
    } catch (err) {
      toast({ title: 'Failed to load Compliance Center', description: err?.message, variant: 'destructive' });
    }
  }, [authHeader, toast]);

  useEffect(() => { load(); }, [load]);

  const updateSetting = useCallback(async (key, value) => {
    setSavingField(key);
    try {
      const r = await axios.put(`${API_URL}/api/risk/settings`, { [key]: value }, authHeader);
      setSettings(r.data);
      toast({ title: 'Settings updated', description: `${key} = ${value}` });
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSavingField(null);
    }
  }, [authHeader, toast]);

  const runSweep = useCallback(async (kind) => {
    setRunning(kind);
    try {
      const url = kind === 'kyc'
        ? `${API_URL}/api/risk/sweep/kyc-expiry`
        : `${API_URL}/api/risk/sweep/rescreening`;
      const r = await axios.post(url, {}, authHeader);
      toast({
        title: kind === 'kyc' ? 'KYC sweep completed' : 'Re-screening completed',
        description: JSON.stringify(r.data),
      });
      await load();
    } catch (err) {
      toast({ title: 'Sweep failed', description: err?.message, variant: 'destructive' });
    } finally {
      setRunning(null);
    }
  }, [authHeader, toast, load]);

  const checkGate = useCallback(async () => {
    if (!gateInput.trim()) return;
    setGateLoading(true);
    setGateResult(null);
    try {
      const r = await axios.get(`${API_URL}/api/risk/gate/${gateInput.trim()}`, authHeader);
      setGateResult(r.data);
    } catch (err) {
      toast({ title: 'Gate check failed', description: err?.message, variant: 'destructive' });
    } finally {
      setGateLoading(false);
    }
  }, [gateInput, authHeader, toast]);

  const refreshSanctions = useCallback(async () => {
    setRefreshingSanctions(true);
    try {
      const r = await axios.post(`${API_URL}/api/risk/sanctions/refresh`, {}, authHeader);
      toast({
        title: `Sanctions refresh ${r.data?.status || 'done'}`,
        description: `Upserted ${r.data?.upserted_total ?? 0} entries.`,
        variant: r.data?.status === 'failed' ? 'destructive' : 'default',
      });
      await load();
    } catch (err) {
      toast({ title: 'Sanctions refresh failed', description: err?.message, variant: 'destructive' });
    } finally {
      setRefreshingSanctions(false);
    }
  }, [authHeader, toast, load]);

  const downloadAuditReport = useCallback(async () => {
    setDownloadingAudit(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/risk/compliance/audit-report/pdf`,
        { ...authHeader, responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '');
      a.download = `Compliance_Audit_Report_${ts}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      toast({ title: 'Audit report generated', description: 'PDF downloaded.' });
    } catch (err) {
      toast({ title: 'Audit report failed', description: err?.message, variant: 'destructive' });
    } finally {
      setDownloadingAudit(false);
    }
  }, [authHeader, toast]);

  if (!settings) {
    return (
      <div className="flex items-center gap-2 p-6 text-slate-500" data-testid="compliance-center-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading Compliance Center…
      </div>
    );
  }

  const isStrict = settings.block_mode === 'strict';

  return (
    <div data-testid="compliance-center-page">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center shadow-lg shadow-rose-500/30">
          <ShieldAlert className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#2B2D42]">Compliance Center</h1>
          <p className="text-sm text-[#6C757D]">
            Central governance for gating, automated sweeps and policy enforcement.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">

        {/* LEFT — Gating Policy */}
        <div className="xl:col-span-2 space-y-5">
          <Card data-testid="compliance-settings-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Settings2 className="w-4 h-4 text-[#FF6B35]" />
                <h3 className="text-sm font-bold uppercase tracking-wide text-[#2B2D42]">Gating Policy</h3>
              </div>

              {/* Block mode */}
              <div className={`p-4 rounded-xl border-2 mb-4 ${
                isStrict ? 'border-rose-300 bg-rose-50' : 'border-amber-300 bg-amber-50'
              }`} data-testid="compliance-block-mode-card">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      {isStrict
                        ? <ShieldAlert className="w-4 h-4 text-rose-600" />
                        : <AlertTriangle className="w-4 h-4 text-amber-600" />}
                      <span className={`text-xs font-bold uppercase ${isStrict ? 'text-rose-700' : 'text-amber-700'}`}>
                        Block mode: {isStrict ? 'STRICT' : 'WARN'}
                      </span>
                    </div>
                    <p className="text-[11px] text-[#6C757D] mt-1 max-w-lg">
                      {isStrict
                        ? 'Operations (Purchase Orders, RFQs, Invoices) are BLOCKED when gating reasons apply.'
                        : 'Operations are allowed but reasons are logged. Switch to STRICT once all counterparties are compliant.'}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant={isStrict ? 'outline' : 'default'}
                    disabled={savingField === 'block_mode'}
                    onClick={() => updateSetting('block_mode', isStrict ? 'warn' : 'strict')}
                    data-testid="compliance-block-mode-toggle"
                    className={!isStrict ? 'bg-rose-600 hover:bg-rose-700 text-white' : ''}>
                    {savingField === 'block_mode' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : isStrict ? 'Switch to WARN' : 'Enable STRICT mode'}
                  </Button>
                </div>
              </div>

              {/* Toggles */}
              <div className="space-y-3">
                {SETTING_FIELDS.map((f) => (
                  <div key={f.key} className="flex items-start justify-between gap-4 p-3 rounded-lg border border-slate-200 bg-white" data-testid={`compliance-setting-${f.key}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#2B2D42]">{f.label}</div>
                      <div className="text-[11px] text-[#6C757D]">{f.description}</div>
                    </div>
                    <Switch
                      checked={!!settings[f.key]}
                      disabled={savingField === f.key}
                      onCheckedChange={(v) => updateSetting(f.key, v)}
                      data-testid={`compliance-switch-${f.key}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Sweep Logs */}
          <Card data-testid="compliance-sweep-logs-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <History className="w-4 h-4 text-[#FF6B35]" />
                <h3 className="text-sm font-bold uppercase tracking-wide text-[#2B2D42]">Recent Sweeps</h3>
              </div>
              {sweepLogs.length === 0 ? (
                <p className="text-xs text-[#6C757D]">No sweep executions yet.</p>
              ) : (
                <ul className="divide-y divide-slate-100" data-testid="compliance-sweep-logs-list">
                  {sweepLogs.map((log, idx) => (
                    <li key={idx} className="py-2.5 flex items-start gap-3" data-testid={`compliance-sweep-log-${idx}`}>
                      <Clock className="w-3.5 h-3.5 text-slate-400 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-[#2B2D42] flex items-center gap-2">
                          {log.kind === 'kyc_expiry' ? 'KYC Expiry Sweep' : 'Periodic Re-screening'}
                          <span className="text-[10px] text-[#6C757D] font-mono">{(log.ran_at || '').slice(0, 19).replace('T', ' ')}</span>
                        </div>
                        <div className="text-[11px] text-[#6C757D] font-mono mt-0.5">
                          {JSON.stringify(log.summary)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Sanctions Data Feed — P5 Pacote C */}
          <Card data-testid="sanctions-feed-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4 text-[#FF6B35]" />
                  <h3 className="text-sm font-bold uppercase tracking-wide text-[#2B2D42]">Sanctions Data Feed</h3>
                </div>
                <Button
                  size="sm"
                  onClick={refreshSanctions}
                  disabled={refreshingSanctions}
                  data-testid="sanctions-refresh-btn"
                  className="bg-[#023E8A] hover:bg-[#012A5E] text-white">
                  {refreshingSanctions
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Refreshing…</>
                    : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh now</>}
                </Button>
              </div>
              <p className="text-[11px] text-[#6C757D] mb-3">
                OpenSanctions feed (OFAC · UN · EU · UAE · FATF). Auto-refreshes every Tuesday 05:00 UTC.
              </p>

              {sanctionsLastRefresh ? (
                <div
                  className={`p-3 rounded-lg border mb-3 ${
                    sanctionsLastRefresh.status === 'ok'
                      ? 'bg-emerald-50 border-emerald-200'
                      : sanctionsLastRefresh.status === 'partial'
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-rose-50 border-rose-200'
                  }`}
                  data-testid="sanctions-last-refresh-panel">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-bold uppercase text-[#2B2D42]">
                      Last refresh · {(sanctionsLastRefresh.started_at || '').slice(0, 19).replace('T', ' ')}
                    </div>
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                        sanctionsLastRefresh.status === 'ok'
                          ? 'bg-emerald-600 text-white'
                          : sanctionsLastRefresh.status === 'partial'
                            ? 'bg-amber-500 text-white'
                            : 'bg-rose-600 text-white'
                      }`}
                      data-testid="sanctions-last-refresh-status">
                      {sanctionsLastRefresh.status || 'unknown'}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#495057] mt-1">
                    Upserted <span className="font-mono font-bold">{sanctionsLastRefresh.upserted_total ?? 0}</span> entries ·
                    {' '}counts: <span className="font-mono">{JSON.stringify(sanctionsLastRefresh.counts || {})}</span>
                  </div>
                  {sanctionsLastRefresh.errors && Object.keys(sanctionsLastRefresh.errors).length > 0 && (
                    <div className="text-[11px] text-rose-700 mt-1 font-mono" data-testid="sanctions-last-refresh-errors">
                      Errors: {JSON.stringify(sanctionsLastRefresh.errors)}
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 text-xs text-[#6C757D] mb-3">
                  No refresh has been executed yet. Click <strong>Refresh now</strong> to pull the latest feed.
                </div>
              )}

              {sanctionsRefreshLogs.length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold uppercase text-[#6C757D] mb-1">Recent refreshes</div>
                  <ul className="divide-y divide-slate-100" data-testid="sanctions-refresh-logs-list">
                    {sanctionsRefreshLogs.map((log, idx) => (
                      <li key={idx} className="py-2 flex items-center gap-2 text-[11px]" data-testid={`sanctions-refresh-log-${idx}`}>
                        <Clock className="w-3 h-3 text-slate-400" />
                        <span className="font-mono text-[#2B2D42]">{(log.started_at || '').slice(0, 19).replace('T', ' ')}</span>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                          log.status === 'ok' ? 'bg-emerald-100 text-emerald-700' :
                          log.status === 'partial' ? 'bg-amber-100 text-amber-700' :
                          'bg-rose-100 text-rose-700'
                        }`}>{log.status}</span>
                        <span className="text-[#6C757D] font-mono">upserted={log.upserted_total ?? 0}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Audit Report — P5 Pacote C */}
          <Card data-testid="audit-report-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <FileDown className="w-4 h-4 text-[#FF6B35]" />
                <h3 className="text-sm font-bold uppercase tracking-wide text-[#2B2D42]">Compliance Audit Report</h3>
              </div>
              <p className="text-[11px] text-[#6C757D] mb-3">
                Formal PDF snapshot of the platform's compliance posture — KYC status, sanctions cache, risk distribution,
                open alerts, latest sweeps and gate policy. Use for regulators, auditors, internal governance committees.
              </p>
              <Button
                size="sm"
                onClick={downloadAuditReport}
                disabled={downloadingAudit}
                data-testid="audit-report-download-btn"
                className="bg-[#FF6B35] hover:bg-[#E85E2A] text-white">
                {downloadingAudit
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Generating PDF…</>
                  : <><FileDown className="w-3.5 h-3.5 mr-1.5" />Download audit report (PDF)</>}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* RIGHT — Sweeps + Gate checker */}
        <div className="space-y-5">
          <Card data-testid="compliance-sweeps-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <PlayCircle className="w-4 h-4 text-[#FF6B35]" />
                <h3 className="text-sm font-bold uppercase tracking-wide text-[#2B2D42]">Manual Sweeps</h3>
              </div>
              <div className="space-y-3">
                <SweepRow
                  icon={Clock}
                  title="KYC Expiry Sweep"
                  description="Flags expired KYC records + emits alerts. Auto-runs daily at 02:00 UTC."
                  loading={running === 'kyc'}
                  onRun={() => runSweep('kyc')}
                  testId="compliance-sweep-kyc"
                />
                <SweepRow
                  icon={TrendingUp}
                  title="Periodic Re-screening"
                  description="Runs sanctions screening over all counterparties. Auto-runs every Monday 03:00 UTC."
                  loading={running === 'rescreen'}
                  onRun={() => runSweep('rescreen')}
                  testId="compliance-sweep-rescreen"
                />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="compliance-gate-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <ShieldAlert className="w-4 h-4 text-[#FF6B35]" />
                <h3 className="text-sm font-bold uppercase tracking-wide text-[#2B2D42]">Gate Checker</h3>
              </div>
              <p className="text-[11px] text-[#6C757D] mb-2">
                Preflight a counterparty ID or legacy supplier ID before issuing a PO/RFQ/Invoice.
              </p>
              <div className="flex gap-2 mb-3">
                <input
                  value={gateInput}
                  onChange={(e) => setGateInput(e.target.value)}
                  placeholder="counterparty_id or supplier_id"
                  data-testid="compliance-gate-input"
                  className="flex-1 text-xs px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:border-[#00B4D8]"
                />
                <Button size="sm" onClick={checkGate} disabled={gateLoading || !gateInput.trim()} data-testid="compliance-gate-check-btn">
                  {gateLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Check'}
                </Button>
              </div>

              {gateResult && (
                <div className={`p-3 rounded-lg border text-xs ${
                  gateResult.allowed
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                    : 'bg-rose-50 border-rose-200 text-rose-700'
                }`} data-testid="compliance-gate-result">
                  <div className="flex items-center gap-1.5 font-semibold mb-1">
                    {gateResult.allowed ? <CheckCircle2 className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
                    {gateResult.allowed ? 'ALLOWED' : 'BLOCKED'} · mode {gateResult.mode?.toUpperCase()}
                  </div>
                  <div className="space-y-0.5 text-[11px]">
                    <div>KYC: <span className="font-mono">{gateResult.kyc_status ?? '—'}</span></div>
                    <div>Risk: <span className="font-mono">{gateResult.risk_level ?? '—'}</span></div>
                    <div>Open sanctions: <span className="font-mono">{gateResult.open_sanctions}</span></div>
                  </div>
                  {(gateResult.reasons || []).length > 0 && (
                    <ul className="mt-2 pt-2 border-t border-current/20 space-y-0.5">
                      {gateResult.reasons.map((r, i) => (
                        <li key={i} className="text-[10px]" data-testid={`compliance-gate-reason-${i}`}>
                          <span className="font-mono font-bold">[{r.severity}]</span> {r.message}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};


const SweepRow = ({ icon: Icon, title, description, loading, onRun, testId }) => (
  <div className="p-3 rounded-lg border border-slate-200 bg-white" data-testid={testId}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5 text-[#023E8A]" />
          <span className="text-sm font-semibold text-[#2B2D42]">{title}</span>
        </div>
        <p className="text-[11px] text-[#6C757D] mt-1">{description}</p>
      </div>
      <Button size="sm" onClick={onRun} disabled={loading} data-testid={`${testId}-run-btn`}>
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Run now'}
      </Button>
    </div>
  </div>
);


export default ComplianceCenter;
